import { useState, useEffect } from "react";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Textarea } from "@/components/ui/Textarea";
import { Button } from "@/components/ui/Button";
import { ToolPageLayout } from "@/components/ToolPageLayout";
import { copyToClipboard } from "@/lib/utils";
import { Copy, RotateCcw, Trash2 } from "lucide-react";

export function PlaceholderImageGenerator() {
  const [w, setW] = useState("600");
  const [h, setH] = useState("400");
  const [bg, setBg] = useState("3b82f6");
  const [fg, setFg] = useState("ffffff");
  const [text, setText] = useState("Placeholder 600x400");
  const [dataUri, setDataUri] = useState("");
  const [copied, setCopied] = useState(false);

  const generate = () => {
    const widthNum = Number(w) || 600;
    const heightNum = Number(h) || 400;
    const cleanBg = bg.startsWith("#") ? bg : `#${bg}`;
    const cleanFg = fg.startsWith("#") ? fg : `#${fg}`;
    const cleanText = text || `${widthNum}x${heightNum}`;

    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${widthNum}" height="${heightNum}" viewBox="0 0 ${widthNum} ${heightNum}">
      <rect width="100%" height="100%" fill="${cleanBg}" />
      <text x="50%" y="50%" font-family="Inter, sans-serif" font-size="${Math.min(widthNum, heightNum) * 0.08}" font-weight="bold" fill="${cleanFg}" dominant-baseline="middle" text-anchor="middle">
        ${cleanText}
      </text>
    </svg>`;

    setDataUri(`data:image/svg+xml;utf8,${encodeURIComponent(svg)}`);
  };

  useEffect(() => {
    generate();
  }, [w, h, bg, fg, text]);

  const handleCopy = () => {
    copyToClipboard(dataUri).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <ToolPageLayout
      title="Placeholder Image Generator - Create Custom Dev Banner Images"
      description="Generate lightweight mock placeholder images with custom widths, heights, text overlays, and hex colors. Produces instant offline inline SVG data URIs."
      h1="Placeholder Image Generator"
      category="Graphics & Formatting"
      categoryPath="/misc-tools"
      howToUse={[
        "Configure your desired image width and height in pixels.",
        "Choose Background and Foreground text color schemes (Hex values).",
        "Type your optional text overlay caption.",
        "Copy the inline data URI or drag-and-drop the generated canvas."
      ]}
      faq={[
        { question: "Is an internet connection needed?", answer: "No. This tool generates pure inline SVG elements inside your local browser memory, with zero reliance on cloud hosts." },
        { question: "How can I import this into my code?", answer: "You can paste the generated data URI directly inside any HTML or React '<img src=\"...\" />' attribute." }
      ]}
    >
      <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto bg-white border border-slate-200 p-6 rounded-2xl shadow-xs">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold text-slate-700">Width (px)</Label>
              <Input value={w} onChange={e => setW(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold text-slate-700">Height (px)</Label>
              <Input value={h} onChange={e => setH(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold text-slate-700">Background Hex</Label>
              <div className="flex gap-1.5 items-center">
                <span className="text-slate-400 font-bold text-sm">#</span>
                <Input value={bg} onChange={e => setBg(e.target.value)} placeholder="3b82f6" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold text-slate-700">Text Color Hex</Label>
              <div className="flex gap-1.5 items-center">
                <span className="text-slate-400 font-bold text-sm">#</span>
                <Input value={fg} onChange={e => setFg(e.target.value)} placeholder="ffffff" />
              </div>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold text-slate-700">Image Caption Text</Label>
            <Input value={text} onChange={e => setText(e.target.value)} />
          </div>
          <div className="pt-4 border-t border-slate-100 space-y-2">
            <div className="flex justify-between items-center">
              <Label className="text-xs font-bold text-slate-500 uppercase">Inline Image Data URI</Label>
              <Button variant="outline" size="sm" onClick={handleCopy} className="text-xs gap-1.5 py-1 h-7">
                <Copy className="w-3.5 h-3.5" />
                {copied ? "Copied!" : "Copy URI"}
              </Button>
            </div>
            <Textarea value={dataUri} readOnly className="font-mono text-[10px] bg-slate-50 min-h-[80px] text-slate-500 leading-relaxed break-all" />
          </div>
        </div>
        <div className="flex flex-col items-center justify-center bg-slate-50 border border-slate-100 rounded-xl overflow-hidden p-4 min-h-[250px]">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 select-none">Live Canvas Preview</span>
          {dataUri && <img src={dataUri} alt="Placeholder preview" className="max-w-full max-h-[220px] object-contain shadow-xs border border-slate-200/50 rounded-lg" />}
        </div>
      </div>
    </ToolPageLayout>
  );
}

const SAMPLE_SVG = `<svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 100 100" enable-background="new 0 0 100 100" xml:space="preserve">
  <!-- This is an XML comment that the optimizer will safely remove -->
  <circle cx="50" cy="50" r="40" fill="#3b82f6" stroke="#1e3a8a" stroke-width="4" />
  <rect x="35" y="35" width="30" height="30" fill="#ffffff" />
</svg>`;

export function SvgOptimizer() {
  const [input, setInput] = useState(SAMPLE_SVG);
  const [output, setOutput] = useState("");
  const [copied, setCopied] = useState(false);

  const process = (val = input) => {
    // Basic structural SVG cleanup
    let min = val.replace(/<!--[\s\S]*?-->/g, '')
                 .replace(/\s+/g, ' ')
                 .replace(/>\s+</g, '><')
                 .trim();
    setOutput(min);
  };

  useEffect(() => {
    process();
  }, []);

  const handleCopy = () => {
    copyToClipboard(output).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <ToolPageLayout
      title="SVG Optimizer Online - Minify XML Vector Graphics"
      description="Optimize, minify, and clean messy or uncompressed SVG graphic code by removing comments, redundant whitespace, and extra line breaks."
      h1="SVG Optimizer"
      category="Graphics & Formatting"
      categoryPath="/misc-tools"
      howToUse={[
        "Paste your vector XML graphic '<svg>...</svg>' code into the left text box.",
        "Click the Optimize Basic button to strip redundant headers or meta tags.",
        "Copy your lightweight, optimized vector output."
      ]}
      faq={[
        { question: "What gets stripped during optimization?", answer: "It removes XML comments, redundant newline breaks, and double tab indentation spacing safely." },
        { question: "Are drawing paths affected?", answer: "No, actual vector geometry paths, scales, dimensions, and fill attributes are fully preserved." }
      ]}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <Button onClick={() => process()} className="bg-slate-900 hover:bg-slate-800 text-white font-medium">Optimize Basic Svg</Button>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => { setInput(SAMPLE_SVG); process(SAMPLE_SVG); }}
              className="text-xs gap-1.5"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Reset Sample
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleCopy}
              disabled={!output}
              className="text-xs gap-1.5"
            >
              <Copy className="w-3.5 h-3.5" />
              {copied ? "Copied!" : "Copy Output"}
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => { setInput(""); setOutput(""); }}
              className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50 gap-1.5"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Clear
            </Button>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-500 uppercase">Raw SVG XML Input</label>
            <Textarea value={input} onChange={e => setInput(e.target.value)} placeholder="<svg>...</svg>" className="min-h-[300px] font-mono text-xs leading-relaxed" />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-500 uppercase">Optimized SVG Output</label>
            <Textarea value={output} readOnly className="min-h-[300px] bg-slate-50 font-mono text-xs text-slate-700 leading-relaxed" />
          </div>
        </div>
      </div>
    </ToolPageLayout>
  );
}

export function MiscTools() {
  return (
    <ToolPageLayout
      title="Misc Image & Development Tools"
      description="Various tools: Placeholder image generator and basic SVG optimizer."
      h1="Miscellaneous Tools"
      category="Misc Tools"
      categoryPath="/misc-tools"
      howToUse={[
        "Select the tool you need.",
        "Enter your input.",
        "Perform the generation or optimization operation."
      ]}
      faq={[
        { question: "Are these tools free?", answer: "Yes, all our misc tools are free." }
      ]}
    >
      <div className="space-y-12">
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs"><h2 className="text-xl font-bold mb-4">Placeholder Image Generator</h2><PlaceholderImageGenerator /></section>
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs"><h2 className="text-xl font-bold mb-4">SVG Optimizer</h2><SvgOptimizer /></section>
      </div>
    </ToolPageLayout>
  );
}
